import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores'
import { firebaseTools } from '@/services/firebase'
import { $vfm, vsToast } from '@isagaco/vuesaga'
import { state } from '@/services/localStorage'
import * as pgArray from 'postgres-array'
import axios from 'axios'
import { consumePendingNavigation, setRouterInstance } from '@/utils/universal-link-navigation'
import { isAppOnV3Host, isOldParentApk } from '@/utils/oldParentApp'
import { createToast, withProps } from 'mosha-vue-toastify'

const handleMaintenanceMode = async () => {
  const { setMaintenanceModeIsOn } = useAuthStore()
  const { data } = await axios.get('https://cloudflare-backend.isaga.workers.dev/config/MAINTENANCE_MODE')
  const maintenanceModeIsOn = data.value === 'true'
  setMaintenanceModeIsOn(maintenanceModeIsOn)
  if (maintenanceModeIsOn) {
    console.log('maintenanceModeIsOn', maintenanceModeIsOn)
     return '/maintenance/'
  }
  return true
}
const loginRequired = async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const authStore = useAuthStore()
  const user = authStore.user
  const authInstanceID = authStore.instanceID as any
  if (isOldParentApk()) return '/mandatoryUpdate/'
  if (user) {
    // const user = await firebaseTools.getCurrentUser()
    if (!user) {
      return `/login?refURL=${to.fullPath !== '/login' ? to.fullPath : ''}`
    }
    const shouldRefresh = !!state.value.jwtRefresh && new Date(state.value.jwtRefresh).getTime() < Date.now()
    const customClaims = await firebaseTools.getCustomClaims(user, shouldRefresh)
    const actualJWT = await firebaseTools.getCurrentUserJWT()
    state.value.jwt = actualJWT.token
    state.value.jwtRefresh = actualJWT.jwtRefresh
    if (typeof customClaims === 'undefined') return false
    // Parents on an old app build that opens v3 must update; staff and admins on one keep working.
    const claimRoles: string[] = customClaims['x-hasura-allowed-roles'] || []
    if (isAppOnV3Host() && !claimRoles.some(role => ['staff', 'instanceAdmin', 'support'].includes(role))) return '/mandatoryUpdate/'

    // Ensure account is loaded before any account-dependent redirects (e.g. suspended)
    try {
      await authStore.ensureAccountLoaded({ user, customClaims })
    } catch (error) {
      console.error('[loginRequired] ensureAccountLoaded failed', error)
    }
    // Billing status check: only for the target instanceID in the route
    const targetInstanceID = to.params.instanceID as string | undefined

    // Allow suspended page to render (has instanceID context)
    if (to.path.includes('/suspended/')) return true

    const getTargetBillingStatus = () => {
      const roles = customClaims['x-hasura-allowed-roles']
      if (roles.includes('support')) return 'active'
      if (!targetInstanceID) return 'active'
      const account: any = authStore.account
      const adminLinks: any[] = account?.administrator || account?.admins || []
      const targetAdmin = adminLinks.find(a => a?.instance?.id === targetInstanceID)
      return targetAdmin?.instance?.customer?.billingStatus || 'active'
    }
    const targetBillingStatus = getTargetBillingStatus()

    if (targetBillingStatus === 'suspended' && targetInstanceID) {
      return `/admin/${targetInstanceID}/suspended/`
    }

    // Show warning toast for admins when target instance billing is in warning state
    const isBillingWarning = targetBillingStatus === 'warning'
    if (isBillingWarning) {
      createToast(
        withProps(vsToast, {
          type: 'warning',
          text: 'Tu organización tiene saldo pendiente. Si no se regulariza, el acceso será restringido.'
        }),
        { hideProgressBar: true, timeout: 8000 }
      )
    }

    const roles = customClaims['x-hasura-allowed-roles']
    const defaultRole = customClaims['x-hasura-default-role']
    const isStaff = window.apk === 'driver' || roles.includes('staff')
    if (isStaff || roles.includes('staff')) {
      if (['/staff/routeGPS/'].includes(to.path)) return true
      if (defaultRole === 'no-roles') {
        const {data:gpsData} = await axios.get(`/gps/+${user?.email?.split('@')[0]}`)
        if (gpsData.tc_devices.length > 0) {
          return router.push(`/staff/routeGPS/`)
        }
      }
      const excludedPaths = ['/staffRoutes/', '/account/', '/staff/routeGPS/', '/staff/vehicleCheck/', '/staff/route/', '/staff/trip/', '/staff/map/'];
      if (!excludedPaths.some(prefix => to.path.startsWith(prefix))) return '/staffRoutes/'
      return true
    } else {
      if (roles.includes('instanceAdmin') || roles.includes('support') || roles.includes('user')) {
        if ((roles.includes('instanceAdmin') || roles.includes('support'))){
          let instanceAdmins
          if (customClaims.version === '2') {
            instanceAdmins = typeof customClaims['x-hasura-admin-instanceIDs'] === 'string' ? 
            pgArray.parse(customClaims['x-hasura-admin-instanceIDs']) : null
          } else {
            instanceAdmins = customClaims['x-hasura-admin-instances']
          }
          const instanceID = instanceAdmins && instanceAdmins.length ? instanceAdmins[0] : '2628d30f-1316-404c-9d7c-38a93c648540'
          if (to.path === '/') {
            return `/admin/${instanceID}/dashboard/`
          }
          if (!to.path.includes('/admin') && !authInstanceID) {
            return `/admin/${instanceID}/dashboard/`
          }
          return true
        }
        if (to.path === '/' && roles.includes('user')) {
          return '/user/map'
        }
        return true
      } else {
        if (to.path !== '/parcelOTP' && !to.path.includes('/enrollmentForm')) return '/parcelOTP' // TODO: this is forbidden
      }
    }
  } else {
    const queryString = Object.keys(to.query)
        .map((key) => `${key}=${to.query[key]}`)
        .join('&');
    const finalQuery = queryString ? `&${queryString}` : ''
    return `/login?refURL=${to.fullPath !== '/login' ? `${to.path}${finalQuery}` : ''}`
  }
  return true
}

// const frillRedirect = async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
//   const { isAuthenticated } = useAuthStore()
//   if (isAuthenticated) {
//     if (to.query.frillRedirect) {
//       return `/sso/frill/?redirect=${to.query.redirect}`
//     } else {
//       return '/'
//     }
//   }
//   return true
// }

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: () => import('../views/NotFound.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/suspended/',
    component: () => import('../views/suspended.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { toolbarHidden: true },
  },
  {
    path: '/mandatoryUpdate/',
    component: () => import('../views/mandatoryUpdate.vue'),
    beforeEnter: [()=>{
      if (!isOldParentApk() && !isAppOnV3Host()) return '/'
    }, handleMaintenanceMode]
  },
  {
    path: '/maintenance/',
    component: () => import('../views/maintenance.vue'),
    beforeEnter: [()=>{
      const { maintenanceModeIsOn } = useAuthStore()
      if (!maintenanceModeIsOn) return '/'
    }]
  },
  {
    path: '/staff/routeGPS/',
    component: () => import('../views/gps.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/parcelOTP',
    component: () => import('../views/parcelOTP.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/staff/map/',
    component: () => import('../views/staffMap.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/staffRoutes/',
    component: () => import('../views/staffRoutes.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'light', statusbarColor: '#2038d5' },
  },
  {
    path: '/staff/vehicleCheck/:vehicleID',
    component: () => import('../views/vehicleCheck.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'light', statusbarColor: '#2038d5' },
  },
  {
    path: '/qrReader/',
    component: () => import('../views/qrReader.vue'),
    beforeEnter: [handleMaintenanceMode],
  },
  {
    path: '/enrollmentForm/:customerAlias/:contract',
    name: 'Registros',
    component: () => import('../views/enrollmentForm.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { toolbarHidden: true },
  },
  {
    path: '/routeRequestForm/:customerAlias/:contract',
    name: 'Solicitud de servicios',
    component: () => import('../views/routeRequestForm.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/enrollment/:customerAlias/:contract',
    name: 'Bienvenida Registros',
    component: () => import('../views/enrollment.vue'),
    beforeEnter: [handleMaintenanceMode],
    meta: { toolbarHidden: true },
  },
  {
    path: '/routeRequest/:customerAlias/:contract',
    name: 'Bienvenida Solicitud de Servicios',
    component: () => import('../views/routeRequest.vue'),
    beforeEnter: [handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/contract/',
    name: 'Contrato',
    component: () => import('../views/contract.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/integrations/',
    name: 'Integraciones',
    component: () => import('../views/integrations.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/plans/:planID',
    name: 'Planeador rutas fijas',
    component: () => import('../views/planner.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/trips/:date',
    name: 'Planeador diario',
    component: () => import('../views/tripPlanner.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/serviceCreation/',
    name: 'Creación de servicios',
    component: () => import('../views/serviceCreation.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/gmap/',
    name: 'google-map',
    component: () => import('../views/gmap.vue'),
    beforeEnter: [handleMaintenanceMode],
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/login.vue'),
    beforeEnter: [handleMaintenanceMode],
    meta: { statusbar: 'light', statusbarColor: '#2038d5' },
  },
  {
    path: '/admin/:instanceID/optimization/',
    name: 'Optimización',
    component: () => import('../views/optimization.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/classicOptimization/',
    name: 'classicOptimization',
    component: () => import('../views/classicOptimization.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/geofences/',
    name: 'Geocercas',
    component: () => import('../views/geofences.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/viewer/',
    name: 'Visualizador',
    component: () => import('../views/viewer.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/vehicleRatings/',
    name: 'Calificaciones',
    component: () => import('../views/vehicleRatings.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/tags/',
    name: 'Etiquetas',
    component: () => import('../views/tags.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/temporals/',
    name: 'Novedades',
    component: () => import('../views/temporals.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/roles/',
    name: 'Roles',
    component: () => import('../views/roles.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/directory/',
    name: 'Directorio',
    component: () => import('../views/directory.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/user/directory/',
    name: 'Directorio Usuario',
    component: () => import('../views/directory.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'default', statusbarColor: '#ffffff' },
  },
  {
    path: '/user/payments/',
    name: 'Pagos usuario',
    component: () => import('../views/userPayments.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'light', statusbarColor: '#2038d5' },
  },
  {
    path: '/user/payments/setup/:instanceID',
    name: 'Pagos instancia',
    component: () => import('../views/userPaymentsInstance.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { toolbarHiddenByQuery: ['sendEvent', 'chargesReminder'] },
  },
  {
    path: '/charges/:chargeID',
    name: 'Pago',
    component: () => import('../views/getCharge.vue'),
    beforeEnter: [handleMaintenanceMode],
    meta: { toolbarHidden: true },
  },
  {
    path: '/admin/:instanceID/notifications/',
    name: 'Comunicados',
    component: () => import('../views/notifications.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/history/',
    name: 'Históricos',
    component: () => import('../views/history.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/tower/',
    name: 'Torre de control',
    component: () => import('../views/tower.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/data/:itemType',
    name: 'Datos',
    component: () => import('../views/table.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/charges/',
    name: 'Charges',
    component: () => import('../views/charges.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/data/:itemType/:itemID',
    name: 'Detalles',
    component: () => import('../views/details.vue'),
    meta: { transition: 'slide-left' },
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/vehicles/consolidate',
    name: 'Consolidado',
    component: () => import('../views/vehiclesConsolidate.vue'),
    meta: { transition: 'slide-left' },
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/dashboard',
    name: 'Tablero',
    component: () => import('../views/dashboard.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/cancelationInbox',
    name: 'Buzón Ausencias',
    component: () => import('../views/cancelationInbox.vue'),
    beforeEnter: [handleMaintenanceMode],
  },
  {
    path: '/admin/:instanceID/addressEditor/:parcelID?/:stopID?',
    name: 'Lugar',
    component: () => import('../views/addressEditor.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
  },
  {
    path: '/user/map/',
    name: 'Acudiente',
    component: () => import('../views/user.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'default', statusbarColor: '#ffffff' },
  },
  {
    path: '/user/inbox/',
    name: 'Buzón',
    component: () => import('../views/inbox.vue'),
    beforeEnter: [handleMaintenanceMode],
    meta: { statusbar: 'default', statusbarColor: '#ffffff' },
  },
  {
    path: '/user/orders/',
    name: 'Reservas',
    component: () => import('../views/orders.vue'),
    beforeEnter: [handleMaintenanceMode],
  },
  {
    path: '/account/',
    name: 'Cuenta',
    component: () => import('../views/account.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'light', statusbarColor: '#2038d5' },
  },
  {
    path: '/staff/trip/:instanceID/:tripID/:tripType/',
    name: 'Recorrido puntual',
    component: () => import('../views/staffTripStream.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: {statusbar: 'light', statusbarColor: '#2038d5'}
  },
  {
    path: '/staff/route/:instanceID/:routeID/:routeType/',
    name: 'Recorrido',
    component: () => import('../views/staffStream.vue'),
    beforeEnter: [loginRequired, handleMaintenanceMode],
    meta: { statusbar: 'default', statusbarColor: '#ffffff' },
  },
  {
    path: '/user/news/',
    name: 'Noticias',
    component: () => import('../views/news.vue'),
  },
  { 
    path: '/:pathMatch(.*)*',
    name: 'NotFound', 
    component: () => import('../views/NotFound.vue'),
    beforeEnter: [handleMaintenanceMode],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes
})

// Register router instance for immediate universal link navigation (iOS)
setRouterInstance(router)

/**
 * Apply route meta configurations
 * Handles statusbar styling, colors, visibility, and other route-specific settings
 */
const applyRouteMeta = (to: RouteLocationNormalized) => {
  // Handle StatusBar configuration
  if (typeof StatusBar !== 'undefined') {
    // Apply statusbar style (light/dark content)
    if (to.meta?.statusbar === 'light') {
      StatusBar.styleLightContent()
    } else {
      StatusBar.styleDefault()
    }

    // Apply statusbar background color if specified
    if (to.meta?.statusbarColor) {
      if (typeof to.meta.statusbarColor === 'string') {
        StatusBar.backgroundColorByHexString(to.meta.statusbarColor)
      }
    }

    // Apply statusbar overlay preference if specified
    if (typeof to.meta?.statusbarOverlay === 'boolean') {
      StatusBar.overlaysWebView(to.meta.statusbarOverlay)
    }

    // Apply statusbar visibility if specified
    if (to.meta?.statusbarHidden === true) {
      StatusBar.hide()
    } else if (to.meta?.statusbarHidden === false) {
      StatusBar.show()
    }
  }

  // Handle toolbar visibility (existing logic)
  if (to.meta?.toolbarHidden && typeof window.os === 'undefined') {
    const authStore = useAuthStore()
    authStore.setToolbarHidden(true)
  }
}

router.beforeEach(async (to, from) => {
  const pendingNav = consumePendingNavigation()
  if (pendingNav?.path) {
    return { path: pendingNav.path, query: pendingNav.query || {} }
  }
  
  // Note: Hash restoration is handled in firebase.ts initFirebase()
  // to avoid infinite loops between router and firebase
  
  try {
    // Apply all route meta configurations (statusbar, toolbar, etc.)
    if ($vfm.openedModals.length > 0) {
      $vfm.hideAll()
      return false
    }
  } catch (error) {
    console.error(error)
  }
})

router.afterEach((to, from) => {
  applyRouteMeta(to)
  const toDepth = to.path.split('/').length
  const fromDepth = from.path.split('/').length
  // The first view replaces the boot screen at once: sliding it in from opacity 0 showed the blank page behind it.
  if (from.matched.length === 0) {
    to.meta.transitionName = undefined
    return
  }
  to.meta.transitionName = toDepth < fromDepth ? 'slide-right' : 'slide-left'
})

router.onError(err => {console.error(err)})

export default router
